// Persona-level fictional data for WeKIT Education Intelligence™ demo.
// All personas are anchored to Greenwood International School.

import type { Insight } from "@/components/schools/AiInsights";

export const student = {
  name: "Aarav Sharma",
  grade: "10",
  section: "B",
  rollNo: "GIS-10B-14",
  house: "Sequoia",
  mentor: "Ms. Radhika Iyer",
  parent: "Mr. & Mrs. Sharma",
  avatarSeed: "aarav",
  hdi: 86,
  cci: 82,
  fri: 78,
  reflectionStreak: 21,
  badges: 14,
  portfolioItems: 9,
};

export const studentDimensions = [
  { dim: "Cognitive", score: 88 },
  { dim: "Social", score: 84 },
  { dim: "Emotional", score: 79 },
  { dim: "Career", score: 82 },
  { dim: "Creativity", score: 86 },
  { dim: "Leadership", score: 74 },
  { dim: "Financial", score: 71 },
  { dim: "AI Literacy", score: 83 },
];

export const studentCareerMatches = [
  { title: "Product Designer", cluster: "Design & Product", fit: 92 },
  { title: "AI Research Engineer", cluster: "AI & Data Science", fit: 88 },
  { title: "Biomedical Innovator", cluster: "Biomedical & Healthcare", fit: 81 },
  { title: "Sustainability Analyst", cluster: "Climate & Sustainability", fit: 76 },
];

export const studentTimeline = [
  { date: "Nov 12", title: "Completed WCC360 Assessment", type: "assessment" },
  { date: "Nov 08", title: "Earned AI Literacy Badge (WAFFS™)", type: "badge" },
  { date: "Nov 04", title: "Reflection: What excites me about design", type: "reflection" },
  { date: "Oct 28", title: "Financial Literacy Module 4 complete", type: "module" },
  { date: "Oct 21", title: "Mentor session with Ms. Iyer", type: "mentor" },
];

export const aiInsightsStudent: Insight[] = [
  {
    tone: "positive",
    title: "Creativity + AI Literacy is a rare combination",
    body: "Aarav scores in the top 12% of his cohort on both creativity and AI literacy. This dual strength maps directly to emerging roles in AI product design.",
    actions: [
      { label: "Open portfolio", to: "/schools/portfolio" },
    ],
  },
  {
    tone: "opportunity",
    title: "Ready for a leadership stretch",
    body: "Emotional and social scores are well above the cohort baseline. A student council or peer-mentoring role this term would compound leadership growth.",
    actions: [
      { label: "Flag for counsellor", to: "/schools/counsellor?student=aarav-sharma" },
    ],
  },
  {
    tone: "watch",
    title: "Financial literacy is trailing his other strengths",
    body: "At 71, financial literacy is Aarav's weakest dimension. Two WAFLE™ modules over the next 3 weeks would bring it in line with his profile.",
    actions: [
      { label: "See WAFLE™ studio", to: "/schools/studios" },
    ],
  },
];

// Parent dashboard — parent of Aarav
export const parentKpis = {
  weeklyReflections: 5,
  moduleProgress: 78,
  mentorSessions: 4,
  clarityIndex: 82,
};

export const parentConversationStarters = [
  "Ask Aarav what he loved most about his last design reflection.",
  "Talk about a family financial decision — WAFLE™ Module 5 covers exactly this.",
  "Explore an AI product he uses daily and what he'd change about it.",
];

export const aiInsightsParent: Insight[] = [
  {
    tone: "positive",
    title: "Aarav is exploring with intent",
    body: "5 weekly reflections and 21-day streak. Students at this consistency show 14 points higher Career Clarity by year-end.",
    actions: [
      { label: "See Aarav's profile", to: "/schools/student" },
    ],
  },
  {
    tone: "opportunity",
    title: "Great time for a family conversation on design",
    body: "His top career match this month is Product Designer (92% fit). A 20-minute weekend chat about design decisions in everyday life will accelerate clarity.",
    actions: [
      { label: "Download parent guide", to: "/parent-guide" },
    ],
  },
];

// Counsellor dashboard
export const counsellorKpis = {
  caseload: 412,
  atRisk: 18,
  scheduledSessions: 24,
  interventionsClosed: 47,
};

export const counsellorQueue = [
  { id: "priya-menon",  name: "Priya Menon", grade: "9-A",  house: "Sequoia", signal: "Clarity index dropped 12 points", priority: "high" as const },
  { id: "kabir-patel",  name: "Kabir Patel", grade: "11-C", house: "Redwood", signal: "Reflection streak broken (5 wks)", priority: "high" as const },
  { id: "neha-rao",     name: "Neha Rao",    grade: "10-B", house: "Cedar",   signal: "Career interest shifted rapidly",   priority: "medium" as const },
  { id: "ishaan-das",   name: "Ishaan Das",  grade: "12-A", house: "Willow",  signal: "Low WAFLE™ engagement",              priority: "medium" as const },
  { id: "ananya-bose",  name: "Ananya Bose", grade: "8-D",  house: "Sequoia", signal: "Emotional score below cohort",       priority: "high" as const },
  { id: "rohan-gupta",  name: "Rohan Gupta", grade: "11-A", house: "Redwood", signal: "Requested mentor change",             priority: "low" as const },
];

export const counsellorCategories = [
  { label: "Career Clarity",    value: 32 },
  { label: "Emotional",         value: 24 },
  { label: "Academic pressure", value: 19 },
  { label: "Peer / Social",     value: 14 },
  { label: "Family",            value: 11 },
];

export const aiInsightsCounsellor: Insight[] = [
  {
    tone: "watch",
    title: "Grade 9 cohort showing early clarity dip",
    body: "6 students in Grade 9 have lost 8+ Career Clarity points this month. A small-group workshop could pre-empt escalation.",
    actions: [
      { label: "Filter queue to Grade 9", to: "/schools/counsellor?grade=9" },
    ],
  },
  {
    tone: "opportunity",
    title: "Reflection nudges are working",
    body: "Students receiving weekly reflection prompts have 3x higher WCC360 completion. Consider extending to all Grade 8 caseload.",
    actions: [
      { label: "Filter queue to Grade 8", to: "/schools/counsellor?grade=8" },
    ],
  },
  {
    tone: "positive",
    title: "47 interventions closed this term",
    body: "89% closure rate on flagged cases. Strongest driver: pairing counsellor sessions with parent conversation starters.",
    actions: [
      { label: "Open Parent view", to: "/schools/parent" },
    ],
  },
];

// Learning Studios
export const studioDetails = [
  {
    key: "WALS",
    name: "WALS™",
    full: "WeKIT Academy of Life Skills",
    color: "from-emerald-500/20 to-emerald-500/5",
    active: 1112,
    completion: 94,
    modules: 12,
    topModules: [
      { title: "Empathy in Action",        completion: 96 },
      { title: "Managing Your Attention",  completion: 92 },
      { title: "Confident Communication",  completion: 89 },
    ],
  },
  {
    key: "WAFLE",
    name: "WAFLE™",
    full: "WeKIT Academy of Financial Literacy",
    color: "from-amber-500/20 to-amber-500/5",
    active: 878,
    completion: 74,
    modules: 8,
    topModules: [
      { title: "How Money Grows",           completion: 82 },
      { title: "Budgeting for Your Goals",  completion: 76 },
      { title: "Understanding Credit",      completion: 68 },
    ],
  },
  {
    key: "WAFFS",
    name: "WAFFS™",
    full: "WeKIT Academy of Future Skills",
    color: "from-primary/20 to-primary/5",
    active: 962,
    completion: 81,
    modules: 10,
    topModules: [
      { title: "Working With AI",           completion: 88 },
      { title: "Systems Thinking",          completion: 79 },
      { title: "Design & Prototyping",      completion: 74 },
    ],
  },
];

export const aiInsightsStudios: Insight[] = [
  {
    tone: "positive",
    title: "WALS™ Empathy module is the standout",
    body: "96% completion and highest reflection quality across the school. Consider anchoring the term's assembly around student stories from this module.",
    actions: [
      { label: "Open Portfolio spotlight", to: "/schools/portfolio" },
    ],
  },
  {
    tone: "watch",
    title: "WAFLE™ Credit module has a completion cliff",
    body: "Completion drops from 76% to 68% at the credit module. A short refreshed intro video and a real-world example would likely close the gap.",
    actions: [
      { label: "Flag to counsellors", to: "/schools/counsellor" },
    ],
  },
];

// Portfolio
export const portfolioSchoolStats = {
  activePortfolios: 1064,
  avgArtifacts: 7.2,
  badgesIssued: 4382,
  reflectionsLogged: 12904,
};

export const portfolioSpotlight = [
  { name: "Aarav Sharma",  grade: "10-B", artifacts: 9, focus: "Design & Product" },
  { name: "Meera Krishnan", grade: "12-A", artifacts: 14, focus: "AI & Data Science" },
  { name: "Rehan Malik",   grade: "11-B", artifacts: 11, focus: "Climate & Sustainability" },
  { name: "Sanya Iyer",    grade: "9-C",  artifacts: 6,  focus: "Media & Storytelling" },
];

export const aiInsightsPortfolio: Insight[] = [
  {
    tone: "opportunity",
    title: "University-ready evidence is compounding",
    body: "1,064 active portfolios averaging 7.2 artifacts. Grade 12 sits at 14 average — strongest signal for early-decision applications.",
    actions: [
      { label: "Filter to Grade 12", to: "/schools/portfolio?grade=12" },
    ],
  },
  {
    tone: "positive",
    title: "Reflections drive artifact quality",
    body: "Students who log a reflection per artifact score 22 points higher on portfolio quality reviews.",
    actions: [
      { label: "See a student profile", to: "/schools/student" },
    ],
  },
];
